import React, { useState, useEffect } from "react"

import Card from "@mui/material/Card"
import MDBox from "components/MDBox"
import MDTypography from "components/MDTypography"
import MDButton from "components/MDButton"
import { InvoiceList, InvoiceDetail } from "."
import { saveAs } from "file-saver"
import * as XLSX from "xlsx-js-style"

interface ManageDeviceUpdateProps {
  onClose: (num: number) => void
  invoiceList?: InvoiceList[]
}

const InvoiceModal: React.FC<ManageDeviceUpdateProps> = ({ onClose, invoiceList }) => {
  const [selectYearMonth, setSelectYearMonth] = useState<string>("")

  useEffect(() => {
    if (!invoiceList) return

    // 請求データが1件以上ある月だけを抽出（本番稼働またはPoCがある場合）
    const validInvoices = invoiceList.filter((invoice) => invoice.invoice_detail_list.length > 0)

    // 最新の月を初期値に設定
    if (validInvoices.length > 0) {
      setSelectYearMonth(validInvoices[validInvoices.length - 1].target_month)
    }
  }, [invoiceList])

  const handleDownloadExcel = () => {
    let invoiceData: InvoiceDetail[] = []
    invoiceList?.forEach((data) => {
      if (data.target_month === selectYearMonth) {
        invoiceData = data.invoice_detail_list
      }
    })
    if (!invoiceData || invoiceData.length === 0) return

    // 列名に「PoC」を追加
    const header = ["サービス提供元名", "不動産業者名", "施設名", "入居者名", "入居者ID", "PoC"]

    // データに判定ロジックを追加
    const body = invoiceData.map((item) => [
      item.catv_name || "",
      item.estate_name || "",
      item.facility_name || "",
      item.medical_record_name || "",
      item.medical_record_id.split("|")[1] || "",
      item.is_poc === 1 ? "○" : "-"
    ])

    // -------------------------------
    // セル単位でスタイルを設定
    // -------------------------------
    const ws: XLSX.WorkSheet = {}
    const setCell = (r: number, c: number, value: string, isHeader = false) => {
      const cellRef = XLSX.utils.encode_cell({ r, c })
      ws[cellRef] = {
        v: value,
        t: "s",
        s: {
          font: { bold: isHeader },
          fill: isHeader ? { patternType: "solid", fgColor: { rgb: "DAF2D0" } } : undefined,
          alignment: { horizontal: "left", vertical: "center" },
          border: {
            top: { style: "thin", color: { rgb: "000000" } },
            bottom: { style: "thin", color: { rgb: "000000" } },
            left: { style: "thin", color: { rgb: "000000" } },
            right: { style: "thin", color: { rgb: "000000" } }
          }
        }
      }
    }

    // ヘッダ
    header.forEach((v, c) => setCell(0, c, v, true))
    // データ
    body.forEach((row: any, r: any) => {
      row.forEach((v: any, c: any) => setCell(r + 1, c, v))
    })

    // 範囲を設定
    ws["!ref"] = XLSX.utils.encode_range({
      s: { r: 0, c: 0 },
      e: { r: body.length, c: header.length - 1 }
    })

    // 列幅固定（1列増えたので設定を追加）
    ws["!cols"] = [
      { wch: 25 },
      { wch: 25 },
      { wch: 25 },
      { wch: 25 },
      { wch: 40 },
      { wch: 10 } // PoC列用
    ]

    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, "請求明細")

    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" })
    const blob = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    })
    saveAs(blob, `invoice_details_${selectYearMonth}.xlsx`)
  }

  return (
    <div className="modal-bg-cover">
      <Card
        sx={{
          width: "90%",
          maxWidth: "500px",
          maxHeight: "calc(100vh - 100px)",
          marginTop: "50px",
          marginBottom: "50px",
          paddingTop: "20px",
          paddingBottom: "20px",
          paddingLeft: "20px"
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            width: "100%",
            padding: "8px 16px",
            boxSizing: "border-box"
          }}
        >
          <MDTypography variant="h4" fontWeight="medium" style={{ flex: 1, textAlign: "left" }}>
            請求情報ダウンロード
          </MDTypography>
          <button
            className="close-button"
            onClick={() => onClose(0)}
            style={{
              position: "absolute",
              right: 16,
              top: 8,
              background: "transparent",
              border: "none",
              fontSize: "24px",
              cursor: "pointer"
            }}
          >
            ×
          </button>
        </div>
        <MDBox style={{ height: "20px" }}></MDBox>
        <MDBox
          style={{
            maxHeight: "calc(100vh - 200px)",
            overflowY: "auto",
            paddingTop: "10px",
            paddingLeft: "5px",
            paddingRight: "25px"
          }}
        >
          <div>
            <select name="yearMonth" value={selectYearMonth} onChange={(e: any) => setSelectYearMonth(e.target.value)}>
              {invoiceList?.map((invoice) =>
                invoice.invoice_detail_list.length > 0 ? (
                  <option key={invoice.target_month} value={invoice.target_month}>
                    {invoice.target_month}
                  </option>
                ) : (
                  <></>
                )
              )}
            </select>
          </div>
          <MDBox mt={4} mb={2}>
            <MDButton
              variant="contained"
              color="info"
              fullWidth
              sx={{
                borderRadius: "4px",
                textTransform: "none",
                fontSize: "1rem",
                padding: "0.75rem 0",
                backgroundColor: "#1976d2",
                "&:hover": {
                  backgroundColor: "#1565c0"
                }
              }}
              onClick={handleDownloadExcel}
            >
              ダウンロード
            </MDButton>
          </MDBox>
        </MDBox>
      </Card>
    </div>
  )
}

export default InvoiceModal
